import { browser } from '$app/environment';
import { awsInit } from '$lib/aws/aws-init';
import { useBookingData } from '$lib/booking-data/booking-data';
import { parseFunction } from '$lib/helpers';
import { useLocalCache } from '$lib/local-cache/local-cache';
import { GRAPHQL_AUTH_MODE, type GraphQLQuery } from '@aws-amplify/api';
import { API } from 'aws-amplify';
import { get, writable } from 'svelte/store';

awsInit();

interface Item {
	id: number;
	name: string;
}

export interface ClassTypeOption extends Item {}

export interface ClassOption extends Item {}

export interface LocationOption extends Item {
	locationTypeId: null | number;
	order: number | null;
}

export interface MarketOption extends Item {}

export interface OfferingOption extends Item {}

export interface ProgramOption extends Item {
	colorDark: string;
	colorLight: string;
}

export interface SemesterOption extends Item {
	dateStart: string | null;
	dateEnd: string | null;
}

export interface StudentAgeOption extends Item {}

export interface ExperienceLevelOption extends Item {}

export interface AppBookingOption {
	classTypes: ClassTypeOption[];
	classes: ClassOption[];
	locations: LocationOption[];
	markets: MarketOption[];
	offerings: OfferingOption[];
	programs: ProgramOption[];
	semesters: SemesterOption[];
	studentsAges: StudentAgeOption[];
	experienceLevels: ExperienceLevelOption[];
}

const fallbackData: AppBookingOption = {
	classes: [],
	classTypes: [],
	experienceLevels: [],
	locations: [],
	markets: [],
	offerings: [],
	programs: [],
	semesters: [],
	studentsAges: []
};

// note: bypass undefined stage because we will make sure this store loaded or failed
// at root layout before reach to any components.
const appBookingOptions = writable<AppBookingOption>();

export const useAppBookingOptions = () => {
	return {
		appBookingOptions
	};
};

// todo: should use local session ?

// auto fetch data when system load
if (browser) {
	const lc = useLocalCache<AppBookingOption>('chessat3-app-booking-options', 1000 * 60 * 60 * 24);

	lc.preferCache(async () => {
		const d = await API.graphql<GraphQLQuery<any>>({
			query: /* GraphQL */ `
				mutation AppBookingOptions($input: AppBookingOptionsInput!) {
					appBookingOptions(input: $input) {
						results {
							programs
							locations
							markets
							classTypes
							studentsAges
							semesters
							experienceLevels
							offerings
							classes
						}
					}
				}
			`,
			variables: { input: { input: JSON.stringify({ workspace_id: 1, locations_ids: [28] }) } },
			authMode: GRAPHQL_AUTH_MODE.API_KEY
		});

		if (d.errors?.length) {
			console.error(d.errors);
			return fallbackData;
		}

		const data = parseFunction(d.data.appBookingOptions.results[0]);

		return {
			classes: data.classes ?? fallbackData.classes,
			classTypes: data.classTypes ?? fallbackData.classTypes,
			experienceLevels: data.experienceLevels ?? fallbackData.experienceLevels,
			locations: data.locations ?? fallbackData.locations,
			markets: data.markets ?? fallbackData.markets,
			offerings: data.offerings ?? fallbackData.offerings,
			programs: data.programs ?? fallbackData.programs,
			semesters: data.semesters ?? fallbackData.semesters,
			studentsAges: data.studentsAges ?? fallbackData.studentsAges
		};
	}).then((d) => appBookingOptions.set(d));
}
